import { Typography, Box, Grid } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { RoundedPaper, StatusBar } from '../../lib/components';
import Util from '../../misc/util';
import { ResetIcon } from '../../lib/icons';
import { BenefitKey } from '../../lib/components/compound/dataDisplay';
import { Button } from '../../lib/components/atomic/button';

export interface SalaryCompareProps {
  netSalaryDefault: number;
  netSalaryBenefit: number;
}

export type BenefitData = Record<BenefitKey, number>;

export interface CompareCardProps extends SalaryCompareProps {
  employeeName?: string;
  grossSalary: number;
  benefitData?: BenefitData;
  onResetClicked: () => void;
}

const column = (costName: string, before: string, after: string, head?: boolean) => {
  const fontWeight = head ? 'medium' : 'normal';
  const color = head ? 'grey.900' : 'primary.main';
  return (
    <Grid container key={costName}>
      <Grid item xs={6}>
        <Typography variant="body2" fontWeight={fontWeight} color={color}>
          {costName}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="body2" fontWeight={fontWeight} color={color} textAlign="right">
          {before}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="body2" fontWeight={fontWeight} color={color} textAlign="right">
          {after}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const CompareCard: FC<CompareCardProps> = ({
  employeeName,
  netSalaryBenefit,
  netSalaryDefault,
  grossSalary,
  benefitData,
  onResetClicked
}) => {
  const { t } = useTranslation();
  const roundBase = 250;
  const buffer = 0.1;
  const maxValueRoundedBase = Math.ceil(netSalaryBenefit / roundBase) * roundBase * (1 + buffer);
  const maxValueDefault = maxValueRoundedBase * (netSalaryBenefit / netSalaryDefault);
  const maxValueBenefit = maxValueRoundedBase;
  const usedBenefitBudget = benefitData
    ? Util.getKeys(benefitData).reduce((sum, key) => sum + benefitData[key], 0)
    : 0;

  return (
    <RoundedPaper
      sx={{
        position: 'absolute',
        height: 'calc(100% - 3rem)',
        width: 'calc(100% - 3rem)',
        padding: '1.5rem 1.5rem 1.5rem 1.5rem'
      }}
    >
      <Box display="flex" flexDirection="column" gap="1.5rem" height="100%">
        <Typography variant="h3">
          {employeeName
            ? t('calculator:COMPARE_CARD_TITLE', { employee: employeeName })
            : t('calculator:COMPARE_CARD_TITLE_NOBODY')}
        </Typography>
        <Box display="flex" flexDirection="column" gap="1rem" paddingBottom=".75rem">
          <StatusBar
            value={netSalaryDefault}
            maxValue={maxValueDefault}
            formattingFn={Util.formatCurrency}
            mainBackgroundColor="grey.100"
            openBackgroundColor="grey.50"
            height={36}
            borderRadius={12}
            label={t('common:BEFORE')}
            decimals={2}
          />
          <StatusBar
            value={netSalaryBenefit}
            maxValue={maxValueBenefit}
            formattingFn={Util.formatCurrency}
            openBackgroundColor="grey.50"
            height={36}
            borderRadius={12}
            label={t('common:AFTER')}
            decimals={2}
          />
        </Box>
        <RoundedPaper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'grey.50',
            padding: '1rem',
            flex: 1
          }}
        >
          <Box paddingBottom="1rem">
            <Typography variant="h3">Detailierte Werte</Typography>
            <Typography>Wie rechnet sich das ganze zusammen</Typography>
          </Box>
          <Grid container paddingBottom=".75rem" gap=".75rem 0">
            {column('Kosten', 'Ohne Benefits', 'Mit Benefits', true)}
          </Grid>
          <Grid
            container
            gap=".75rem 0"
            sx={{
              overflow: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' }
            }}
          >
            {column(
              'Buttogehalt',
              Util.formatCurrency(grossSalary + usedBenefitBudget),
              Util.formatCurrency(grossSalary)
            )}
            {benefitData &&
              Object.keys(benefitData)
                .filter((key) => benefitData[key as BenefitKey] > 0)
                .map((key) =>
                  column(
                    t(`benefits:${key as BenefitKey}`),
                    '0,00€',
                    Util.formatCurrency(benefitData[key as BenefitKey])
                  )
                )}
          </Grid>
        </RoundedPaper>
        <Box>
          <Button variant="secondary" onClick={onResetClicked}>
            <ResetIcon />
            {t('common:RESET')}
          </Button>
        </Box>
      </Box>
    </RoundedPaper>
  );
};
